// `PaymentMethod` — Stripe Elements card-update flow (US-197).
//
// Two surfaces:
//   - `<PaymentMethodRow />` — read-only summary with "Modifier la carte"
//     CTA. Renders inline with the rest of Abonnement.
//   - `<PaymentMethodModal />` — opens on CTA click, mounts the Stripe
//     Elements provider with a fresh SetupIntent, captures the new card,
//     and confirms. Stripe sends `customer.updated` → webhook syncs our
//     `paymentMethodBrand/Last4` columns.
//
// Stripe Elements lazy-loads via `@stripe/stripe-js` (shared loader in
// `@/lib/stripe`). The publishable key is read from
// `import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY`. If absent, the modal
// renders a graceful inline error.

import { useEffect, useMemo, useState } from 'react';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { type StripeElementsOptions } from '@stripe/stripe-js';

import { useUpdatePaymentMethod } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Banner,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  toast,
} from '@barel/ui';

import { getStripe, publishableKey } from '@/lib/stripe';

export interface PaymentMethodRowProps {
  brand: string | null;
  last4: string | null;
  onEdit: () => void;
}

export function PaymentMethodRow({ brand, last4, onEdit }: PaymentMethodRowProps) {
  const t = useT('billing');
  return (
    <div
      data-component="PaymentMethodRow"
      className="flex items-center justify-between border-b border-default py-3"
    >
      <div className="flex flex-col gap-0.5">
        <div className="text-sm font-medium text-primary">{t('paymentMethod.title')}</div>
        <div className="text-xs text-tertiary">
          {brand && last4 ? t('paymentMethod.current', { brand, last4 }) : t('paymentMethod.none')}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onEdit} data-testid="payment-method-edit">
        {t('paymentMethod.edit')}
      </Button>
    </div>
  );
}

export interface PaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
}

export function PaymentMethodModal({ open, onClose }: PaymentMethodModalProps) {
  const t = useT('billing');
  const setupIntent = useUpdatePaymentMethod();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [pkMissing, setPkMissing] = useState(false);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setPkMissing(false);
      return;
    }
    if (!publishableKey()) {
      setPkMissing(true);
      return;
    }
    setupIntent.mutate(undefined, {
      onSuccess: (res) => setClientSecret(res.clientSecret),
      onError: () => toast({ tone: 'error', title: t('paymentMethod.errorToast') }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options: StripeElementsOptions | undefined = useMemo(
    () => (clientSecret ? { clientSecret } : undefined),
    [clientSecret],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent size="md" data-testid="payment-method-modal">
        <DialogHeader title={t('paymentMethod.modalTitle')} />
        <DialogBody>
          {pkMissing ? (
            <Banner tone="warning">
              Stripe publishable key not configured. Set
              <code className="mx-1 rounded bg-bg-elevated px-1">VITE_STRIPE_PUBLISHABLE_KEY</code>
              in your env.
            </Banner>
          ) : !clientSecret ? (
            <p className="text-sm text-secondary">Loading…</p>
          ) : (
            <Elements stripe={getStripe()} options={options}>
              <CardCaptureForm onClose={onClose} />
            </Elements>
          )}
        </DialogBody>
        {(pkMissing || !clientSecret) && (
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t('upgrade.cancel')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CardCaptureForm({ onClose }: { onClose: () => void }) {
  const t = useT('billing');
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });
    setSubmitting(false);
    if (result.error) {
      toast({
        tone: 'error',
        title: t('paymentMethod.errorToast'),
        description: result.error.message,
      });
      return;
    }
    toast({ tone: 'success', title: t('paymentMethod.successToast') });
    onClose();
  };

  return (
    <form onSubmit={onSubmit} data-testid="payment-method-form">
      <PaymentElement />
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          {t('upgrade.cancel')}
        </Button>
        <Button type="submit" variant="primary" size="sm" loading={submitting}>
          {t('paymentMethod.saveCta')}
        </Button>
      </div>
    </form>
  );
}
